import { useState } from 'react';
import { Chip, fmtDate, usd } from '../bids/FieldGrid';
import { card, useList, sourceLabel, CHANNEL_LABEL, CONTENT_STATUS, CONTENT_TONE, EVENT_STATUS, EVENT_TONE, Empty } from './shared';
import { dealStepLabel } from '../../lib/dealStep.mjs';

// Fixed source -> colour slot (validated reference categorical palette, light / dark).
// Colour follows the source, never its rank, so a month with fewer sources keeps its colours.
const SOURCE_COLOR = {
  contact_form: ['#2a78d6', '#3987e5'],
  'datasheet-download': ['#eb6834', '#d95926'],
  chatbot: ['#1baf7a', '#199e70'],
  website: ['#eda100', '#c98500'],
  portal_manual: ['#e87ba4', '#d55181'],
  trade_show: ['#4a3aa7', '#9085e9'],
};
const OTHER_COLOR = ['#8a8a85', '#9a9a94'];
const isDark = () => typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
const colorFor = (s) => (SOURCE_COLOR[s] || OTHER_COLOR)[isDark() ? 1 : 0];
const monthLabel = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });

function Stat({ label, value, hint }) {
  return (
    <div className={card}>
      <div className="text-xs font-medium text-[var(--text-secondary)]">{label}</div>
      <div className="mt-1 text-2xl font-semibold text-[var(--text-primary)]">{value}</div>
      {hint && <div className="mt-0.5 text-xs text-[var(--text-secondary)]">{hint}</div>}
    </div>
  );
}

function LeadsBySourceChart({ data }) {
  const [asTable, setAsTable] = useState(false);
  const [hover, setHover] = useState(null);
  const sources = data.sources;
  const max = Math.max(1, ...data.bySourceMonth.map((r) => r.total));
  const H = 160;
  return (
    <div className={card}>
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-[var(--text-primary)]">Leads by source, last 12 months</h3>
        <button className="text-xs text-primary underline" onClick={() => setAsTable((v) => !v)}>{asTable ? 'Show chart' : 'Show table'}</button>
      </div>
      {sources.length > 1 && (
        <div className="mb-3 flex flex-wrap gap-3 text-xs text-[var(--text-secondary)]">
          {sources.map((s) => (
            <span key={s} className="inline-flex items-center gap-1.5"><span className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: colorFor(s) }} />{sourceLabel(s)}</span>
          ))}
        </div>
      )}
      {asTable ? (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead><tr className="text-left text-[var(--text-secondary)]"><th className="py-1 pr-2">Month</th>{sources.map((s) => <th key={s} className="py-1 pr-2">{sourceLabel(s)}</th>)}<th className="py-1">Total</th></tr></thead>
            <tbody>
              {data.bySourceMonth.map((r) => (
                <tr key={r.month} className="border-t border-[var(--border)]"><td className="py-1 pr-2">{monthLabel(r.month)}</td>{sources.map((s) => <td key={s} className="py-1 pr-2">{r[s] || 0}</td>)}<td className="py-1 font-medium">{r.total}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="relative">
          <div className="flex items-end gap-1.5 border-b border-[var(--border)]" style={{ height: H }}>
            {data.bySourceMonth.map((r) => (
              <div key={r.month} className="flex h-full flex-1 flex-col justify-end" onMouseEnter={() => setHover(r)} onMouseLeave={() => setHover(null)}>
                <div className="flex flex-col-reverse gap-[2px]">
                  {sources.filter((s) => r[s]).map((s, i, arr) => (
                    <div key={s} style={{ height: Math.max(2, (r[s] / max) * (H - 20)), background: colorFor(s), borderRadius: i === arr.length - 1 ? '4px 4px 0 0' : 0 }} />
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-1 flex gap-1.5">
            {data.bySourceMonth.map((r) => <div key={r.month} className="flex-1 text-center text-[10px] text-[var(--text-secondary)]">{monthLabel(r.month)}</div>)}
          </div>
          {hover && (
            <div className="pointer-events-none absolute right-0 top-0 rounded-lg border border-[var(--border)] bg-white p-2 text-xs shadow dark:bg-slate-800">
              <div className="font-semibold text-[var(--text-primary)]">{monthLabel(hover.month)}: {hover.total} lead{hover.total === 1 ? '' : 's'}</div>
              {sources.filter((s) => hover[s]).map((s) => <div key={s} className="text-[var(--text-secondary)]"><span className="mr-1 inline-block h-2 w-2 rounded-sm" style={{ background: colorFor(s) }} />{sourceLabel(s)}: {hover[s]}</div>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function TopList({ title, rows }) {
  return (
    <div>
      <div className="mb-1 text-xs font-medium text-[var(--text-secondary)]">{title}</div>
      {rows.length ? (
        <ul className="space-y-0.5 text-sm">{rows.slice(0, 6).map((r) => <li key={r.key} className="flex justify-between gap-2"><span className="truncate">{r.key}</span><span className="text-[var(--text-secondary)]">{r.count}</span></li>)}</ul>
      ) : <div className="text-xs text-[var(--text-secondary)]">None recorded</div>}
    </div>
  );
}

export default function DashboardTab() {
  const { rows: d, err } = useList('/marketing/dashboard', null);
  if (err) return <p className="text-sm text-rose-600">{err}</p>;
  if (!d) return <p className="text-sm text-[var(--text-secondary)]">Loading…</p>;
  const a = d.attribution;
  return (
    <div className="space-y-4">
      {!d.installed && <p className="rounded-lg bg-amber-50 p-3 text-sm text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">Marketing tables are not installed yet. Leads below come straight from the website table.</p>}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat label="Website leads (all time)" value={a.website_leads} hint={`${d.firstTouch.with_utm} with campaign tags`} />
        <Stat label="Linked to a CRM company or contact" value={a.linked_to_crm} hint={a.website_leads ? `${Math.round((a.linked_to_crm / a.website_leads) * 100)}% of website leads` : ''} />
        <Stat label="Reached a quote" value={a.with_quote} hint={`${a.accepted} accepted`} />
        <Stat label="Won" value={a.won} hint={a.won_value ? `$${Math.round(a.won_value).toLocaleString()} estimated value` : ''} />
      </div>
      <LeadsBySourceChart data={d} />
      <div className="grid gap-4 md:grid-cols-2">
        <div className={card}>
          <h3 className="mb-3 text-sm font-semibold text-[var(--text-primary)]">First touch (website leads)</h3>
          <div className="grid grid-cols-2 gap-4">
            <TopList title="UTM source" rows={d.firstTouch.utm_source} />
            <TopList title="UTM campaign" rows={d.firstTouch.utm_campaign} />
            <TopList title="Referrer" rows={d.firstTouch.referrer} />
            <TopList title="Landing page" rows={d.firstTouch.landing_page} />
          </div>
        </div>
        <div className={card}>
          <h3 className="mb-3 text-sm font-semibold text-[var(--text-primary)]">Website source to deal</h3>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-[var(--text-secondary)]"><th className="py-1">Source</th><th>Leads</th><th>Linked</th><th>Quoted</th><th>Won</th></tr></thead>
            <tbody>{a.by_source.map((s) => <tr key={s.source} className="border-t border-[var(--border)]"><td className="py-1">{sourceLabel(s.source)}</td><td>{s.leads}</td><td>{s.linked}</td><td>{s.quoted}</td><td>{s.won}</td></tr>)}</tbody>
          </table>
          {!a.quote_data && <p className="mt-2 text-xs text-[var(--text-secondary)]">Quotes are not installed, so the quoted column counts only legacy proposals.</p>}
          {a.rows.length > 0 && (
            <ul className="mt-3 space-y-1 text-xs">
              {a.rows.slice(0, 8).map((r) => <li key={r.id} className="flex justify-between gap-2"><span className="truncate">{r.company || r.name} · {sourceLabel(r.source)}{r.utm_source ? ` · ${r.utm_source}` : ''}</span><span className="shrink-0 text-[var(--text-secondary)]">{dealStepLabel(r)}</span></li>)}
            </ul>
          )}
        </div>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <div className={card}>
          <h3 className="mb-3 text-sm font-semibold text-[var(--text-primary)]">Content pipeline</h3>
          {d.content ? (
            <>
              <div className="mb-3 flex flex-wrap gap-2">{Object.keys(CONTENT_STATUS).map((s) => <Chip key={s} tone={CONTENT_TONE[s]}>{CONTENT_STATUS[s]}: {d.content.byStatus[s] || 0}</Chip>)}</div>
              {d.content.next.length ? (
                <ul className="space-y-1 text-sm">{d.content.next.map((c) => <li key={c.id} className="flex justify-between gap-2"><span className="truncate">{c.title}</span><span className="shrink-0 text-xs text-[var(--text-secondary)]">{CHANNEL_LABEL[c.channel]} · {CONTENT_STATUS[c.status]}{c.target_date ? ` · ${fmtDate(c.target_date)}` : ''}</span></li>)}</ul>
              ) : <Empty>No drafts waiting.</Empty>}
            </>
          ) : <Empty>Not installed yet.</Empty>}
        </div>
        <div className={card}>
          <h3 className="mb-3 text-sm font-semibold text-[var(--text-primary)]">Upcoming events</h3>
          {d.events ? (
            <>
              {d.events.upcoming.length ? (
                <ul className="space-y-1 text-sm">{d.events.upcoming.map((e) => <li key={e.id} className="flex items-center justify-between gap-2"><span className="truncate">{e.name}</span><span className="flex shrink-0 items-center gap-2 text-xs text-[var(--text-secondary)]">{e.start_date ? fmtDate(e.start_date) : 'Date not set'} <Chip tone={EVENT_TONE[e.status]}>{EVENT_STATUS[e.status]}</Chip></span></li>)}</ul>
              ) : <Empty>No upcoming events.</Empty>}
              <p className="mt-2 text-xs text-[var(--text-secondary)]">CCS share of event costs dated {d.events.year}: {usd(d.events.cost_this_year_cents) || '$0.00'}</p>
            </>
          ) : <Empty>Not installed yet.</Empty>}
        </div>
      </div>
      <p className="text-xs text-[var(--text-secondary)]">People met at events are added in the Events tab. GA4 is still a placeholder on the website, so traffic numbers come from the portal dashboard's site-traffic card.</p>
    </div>
  );
}
